/** /notification_features/ + /notification_feature_flags/ — leveled feature
 * flags gating notification-block rendering.
 *
 * A `Notification_Block` may point at a feature (registry codename); the block
 * renders only while the feature is enabled at EVERY applicable level
 * (system → OTA → tenant → tour → notification → tag). Flag rows are SPARSE
 * overrides — absence of a row means enabled — so the grid lists explicit
 * overrides only. There is deliberately no delete (single or batch):
 * revert-to-default = toggle `enabled` back on.
 *
 * level=tag rows are a CONDITION, not an on/off override: one row per feature
 * holds tag chips (+ match mode + sources), the feature renders only when the
 * configured tags are present in the rendering context, and `enabled` is the
 * condition's master switch — true = enforced, false = suspended.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { WireNode } from '@/components/QuerySearch';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export interface NotificationFeatureDef {
  codename: string;
  name: string;
  description: string;
}

export type FeatureFlagLevel = 'system' | 'ota' | 'tenant' | 'tour' | 'notification' | 'tag';

export interface FeatureLevelDef {
  codename: FeatureFlagLevel;
  name: string;
}

/** Registry {codename, name} pair — tag sources and match modes share it. */
export interface TagSourceDef {
  codename: string;
  name: string;
}

export interface FeatureRegistry {
  features: NotificationFeatureDef[];
  levels: FeatureLevelDef[];
  /** Entities whose tags a level=tag row may draw from (registry-defined). */
  tag_sources: TagSourceDef[];
  /** Allowed tag_match_mode values with display names. */
  tag_match_modes: TagSourceDef[];
}

export function getRegistry(): Promise<FeatureRegistry> {
  return apiGet('/notification_features/');
}

// ---------------------------------------------------------------------------
// Flag rows
// ---------------------------------------------------------------------------

export interface NamedScopeRef {
  id: number;
  name: string | null;
}

export interface UserRef {
  id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

/** Resolved reference of a pinned (concrete) tag chip. */
export interface TagRefEmbed {
  id: number;
  name: string;
  /** Soft-deleted tags never match (kept visible so a stale chip is diagnosable). */
  is_deleted?: boolean;
  category: { id: number; code: string; name: string };
  /** Owning tenant of the tag; null for a system tag. */
  tenant: TenantRef | null;
}

/** level=tag polarity. `require` is the historical meaning and the default:
 *  the feature renders only while the chips match. `exclude` inverts it —
 *  what "never send the app promo to Viator" needs. */
export type FlagPolarity = 'require' | 'exclude';

/** level=tag channel scope. '' = every channel, which is how every row behaved
 *  before the field existed; a channel-scoped row does not apply to the
 *  canonical base render. */
export type FlagChannel = '' | 'email' | 'sms' | 'chat' | 'whatsapp' | 'airbnb';

export const FLAG_CHANNELS: FlagChannel[] = ['', 'email', 'sms', 'chat', 'whatsapp', 'airbnb'];

/** One chip of a level=tag row: a pinned tag ref XOR a by-code spec. */
export type FlagTagEntry = { tag: TagRefEmbed } | { category_code: string; tag_name: string };

export interface FeatureFlagListItem {
  id: number;
  feature: string;
  level: FeatureFlagLevel;
  tenant: TenantRef | null;
  ota: NamedScopeRef | null;
  tour: NamedScopeRef | null;
  notification: NamedScopeRef | null;
  /** level=tag only: the condition's chips in configured order; null elsewhere. */
  tags: FlagTagEntry[] | null;
  /** level=tag only: and = all chips must match, or = any chip; null elsewhere. */
  tag_match_mode: 'and' | 'or' | null;
  /** level=tag only: registry codenames of the in-context entities; null elsewhere. */
  tag_sources: string[] | null;
  /** level=tag only: `require` = the chips must match, `exclude` = they must NOT. */
  polarity: FlagPolarity | null;
  /** level=tag only: '' = every channel; otherwise this delivery channel only. */
  channel: FlagChannel | null;
  /** True when the reseller-policy compiler wrote this row — editing it will be
   *  reported as diverged on the next recompile rather than silently overwritten. */
  compiled?: boolean;
  enabled: boolean;
  comment: string;
  updated_by: UserRef | null;
  updated_ip: string | null;
  created_ts: string | null;
  updated_ts: string | null;
}

/** Write shape of one tag chip: {tag: <id>} XOR {category_code, tag_name}. */
export type FlagTagWrite = { tag: number } | { category_code: string; tag_name: string };

export interface FeatureFlagCreate {
  feature: string;
  level: FeatureFlagLevel;
  tour_operator?: number | null;
  ota?: number | null;
  tour?: number | null;
  notification?: number | null;
  /** Required for level=tag (rejected on other levels). */
  tags?: FlagTagWrite[];
  /** level=tag only; defaults to and. */
  tag_match_mode?: 'and' | 'or';
  /** Required for level=tag: non-empty subset of the registry's tag_sources. */
  tag_sources?: string[];
  /** level=tag only; defaults to require. An `exclude` row with no chips is a
   *  400 — it would suppress the feature for every booking. */
  polarity?: FlagPolarity;
  /** level=tag only; defaults to '' (every channel). */
  channel?: FlagChannel;
  enabled?: boolean;
  comment?: string;
}

/** Only `enabled`/`comment` are mutable — feature/level/scope are immutable
 *  (create a new flag instead) — EXCEPT level=tag rows, whose `tags` (a
 *  wholesale replace), `tag_match_mode`, `tag_sources`, `polarity` and
 *  `channel` stay editable: with no DELETE, an immutable wrong tag set or
 *  polarity would be a dead end. */
export interface FeatureFlagUpdate {
  enabled?: boolean;
  comment?: string;
  tags?: FlagTagWrite[];
  tag_match_mode?: 'and' | 'or';
  tag_sources?: string[];
  polarity?: FlagPolarity;
  channel?: FlagChannel;
}

export interface ListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  filters?: WireNode[];
}

function toQuery(p: ListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  return usp.toString();
}

export function list(p: ListParams = {}): Promise<PaginatedResponse<FeatureFlagListItem>> {
  const qs = toQuery(p);
  return apiGet(`/notification_feature_flags/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

export function create(body: FeatureFlagCreate): Promise<FeatureFlagListItem> {
  return apiPost('/notification_feature_flags/', body);
}

export function update(id: number, body: FeatureFlagUpdate): Promise<FeatureFlagListItem> {
  return apiPatch(`/notification_feature_flags/${id}/`, body);
}

// ---------------------------------------------------------------------------
// Validate (per-level checks for a feature in a context)
// ---------------------------------------------------------------------------

export interface ValidateContext {
  mode: 'json' | 'event_delivery_log' | 'entity';
  json?: string;
  event_delivery_log_id?: string;
  entity_kind?: 'user' | 'gtu' | 'order';
  entity_id?: number;
}

export interface ValidateRequest {
  feature: string;
  notification?: number | null;
  context: ValidateContext;
  /** Explicit scope-id overrides — always win over context-derived entities. */
  scopes?: {
    tour_operator?: number | null;
    ota?: number | null;
    tour?: number | null;
  };
}

export interface FeatureCheck {
  level: FeatureFlagLevel;
  applicable: boolean;
  scope: { kind: string | null; id: number | null; label: string } | null;
  row_found: boolean;
  row_id: number | null;
  /** Effective verdict for this level; null when not applicable. */
  enabled: boolean | null;
  comment: string;
  /** This check determined a DISABLED verdict. */
  decisive: boolean;
  reason: string;
  /** level=tag only (null elsewhere): structured tag-condition verdict. */
  detail?: {
    mode: string;
    sources: string[];
    chips: Array<{ kind: string; label: string; matched: boolean; resolved: boolean }>;
    source_counts?: Record<string, number>;
  } | null;
}

export interface ValidateResult {
  feature: string;
  enabled: boolean;
  reason: string;
  checks: FeatureCheck[];
}

export function validateFeature(body: ValidateRequest): Promise<ValidateResult> {
  return apiPost('/notification_features/validate/', body);
}
